/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from '../../../../core';
import { mergeHeaders, mergeOnlyDefinedHeaders } from '../../../../core/headers';
import * as environments from '../../../../environments';
import * as errors from '../../../../errors/index';
import * as serializers from '../../../../serialization/index';
import * as Truvity from '../../../index';

export declare namespace PublishedSchemas {
    export interface Options {
        environment?: core.Supplier<environments.TruvityEnvironment | string>;
        /** Specify a custom URL to connect the client to. */
        baseUrl?: core.Supplier<string>;
        apiKey?: core.Supplier<string | undefined>;
        /** Additional headers to include in requests. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
        fetcher?: core.FetchFunction;
    }

    export interface RequestOptions {
        /** The maximum time to wait for a response in seconds. */
        timeoutInSeconds?: number;
        /** The number of times to retry the request. Defaults to 2. */
        maxRetries?: number;
        /** A hook to abort the request. */
        abortSignal?: AbortSignal;
        /** Additional query string parameters to include in the request. */
        queryParams?: Record<string, unknown>;
        /** Additional headers to include in the request. */
        headers?: Record<string, string | core.Supplier<string | undefined> | undefined>;
    }
}

/**
 * The API to manage Truvity's published credential schemas.
 */
export class PublishedSchemas {
    protected readonly _options: PublishedSchemas.Options;

    constructor(_options: PublishedSchemas.Options = {}) {
        this._options = _options;
    }

    /**
     * Search for PublishedSchemas with extended filters capabilities.
     *
     * @param {Truvity.PublishedSchemaSearchRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaSearch()
     */
    public publishedSchemaSearch(
        request: Truvity.PublishedSchemaSearchRequest = {},
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ListPublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(this.__publishedSchemaSearch(request, requestOptions));
    }

    private async __publishedSchemaSearch(
        request: Truvity.PublishedSchemaSearchRequest = {},
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ListPublishedCredentialSchema>> {
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({ ...(await this._getCustomAuthorizationHeaders()) }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                'published-schemas/search',
            ),
            method: 'POST',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.PublishedSchemaSearchRequest.jsonOrThrow(request, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ListPublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling POST /published-schemas/search.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Get the latest version of PublishedSchema.
     *
     * @param {string} id - The ID of the resource.
     * @param {Truvity.PublishedSchemaLatestRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaLatest("id")
     */
    public publishedSchemaLatest(
        id: string,
        request: Truvity.PublishedSchemaLatestRequest = {},
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourcePublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(this.__publishedSchemaLatest(id, request, requestOptions));
    }

    private async __publishedSchemaLatest(
        id: string,
        request: Truvity.PublishedSchemaLatestRequest = {},
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourcePublishedCredentialSchema>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `published-schemas/${encodeURIComponent(id)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourcePublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling GET /published-schemas/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Delete PublishedSchema's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.PublishedSchemaDeleteRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaDelete("id", {
     *         ifMatch: "If-Match"
     *     })
     */
    public publishedSchemaDelete(
        id: string,
        request: Truvity.PublishedSchemaDeleteRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<void> {
        return core.HttpResponsePromise.fromPromise(this.__publishedSchemaDelete(id, request, requestOptions));
    }

    private async __publishedSchemaDelete(
        id: string,
        request: Truvity.PublishedSchemaDeleteRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<void>> {
        const { idempotencyKey, ifMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `published-schemas/${encodeURIComponent(id)}`,
            ),
            method: 'DELETE',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return { data: undefined, rawResponse: _response.rawResponse };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError('Timeout exceeded when calling DELETE /published-schemas/{id}.');
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update annotations of PublishedSchema's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.PublishedSchemaUpdateAnnotationsRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaUpdateAnnotations("id", {
     *         ifMatch: "If-Match",
     *         annotations: {
     *             "key": "value"
     *         }
     *     })
     */
    public publishedSchemaUpdateAnnotations(
        id: string,
        request: Truvity.PublishedSchemaUpdateAnnotationsRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourcePublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(
            this.__publishedSchemaUpdateAnnotations(id, request, requestOptions),
        );
    }

    private async __publishedSchemaUpdateAnnotations(
        id: string,
        request: Truvity.PublishedSchemaUpdateAnnotationsRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourcePublishedCredentialSchema>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `published-schemas/${encodeURIComponent(id)}/annotations`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.PublishedSchemaUpdateAnnotationsRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourcePublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling PATCH /published-schemas/{id}/annotations.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Update labels of PublishedSchema's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.PublishedSchemaUpdateLabelsRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.UnsupportedMediaTypeError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaUpdateLabels("id", {
     *         ifMatch: "If-Match",
     *         labels: {
     *             "key": "value"
     *         }
     *     })
     */
    public publishedSchemaUpdateLabels(
        id: string,
        request: Truvity.PublishedSchemaUpdateLabelsRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourcePublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(this.__publishedSchemaUpdateLabels(id, request, requestOptions));
    }

    private async __publishedSchemaUpdateLabels(
        id: string,
        request: Truvity.PublishedSchemaUpdateLabelsRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourcePublishedCredentialSchema>> {
        const { idempotencyKey, ifMatch, ..._body } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `published-schemas/${encodeURIComponent(id)}/labels`,
            ),
            method: 'PATCH',
            headers: _headers,
            contentType: 'application/json',
            queryParameters: requestOptions?.queryParams,
            requestType: 'json',
            body: serializers.PublishedSchemaUpdateLabelsRequest.jsonOrThrow(_body, {
                unrecognizedObjectKeys: 'strip',
                omitUndefined: true,
            }),
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourcePublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 415:
                    throw new Truvity.UnsupportedMediaTypeError(
                        serializers.UnsupportedMediaTypeProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling PATCH /published-schemas/{id}/labels.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Restore PublishedSchema's resource.
     *
     * @param {string} id - The primary and unique identifier of the resource (inside tenant)
     *                      according to [RFC 4122](https://www.rfc-editor.org/info/rfc4122).
     * @param {Truvity.PublishedSchemaRestoreRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.MethodNotAllowedError}
     * @throws {@link Truvity.PreconditionFailedError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaRestore("id", {
     *         ifMatch: "If-Match"
     *     })
     */
    public publishedSchemaRestore(
        id: string,
        request: Truvity.PublishedSchemaRestoreRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourcePublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(this.__publishedSchemaRestore(id, request, requestOptions));
    }

    private async __publishedSchemaRestore(
        id: string,
        request: Truvity.PublishedSchemaRestoreRequest,
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourcePublishedCredentialSchema>> {
        const { idempotencyKey, ifMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'Idempotency-Key': idempotencyKey != null ? idempotencyKey : undefined,
                'If-Match': ifMatch,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `published-schemas/${encodeURIComponent(id)}/restore`,
            ),
            method: 'POST',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourcePublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 405:
                    throw new Truvity.MethodNotAllowedError(
                        serializers.MethodNotAllowedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 412:
                    throw new Truvity.PreconditionFailedError(
                        serializers.PreconditionFailedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling POST /published-schemas/{id}/restore.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    /**
     * Read the specific version of the PublishedSchema's resource.
     *
     * @param {string} id - The ID of the resource.
     * @param {number} revision - The revision of the resource.
     * @param {Truvity.PublishedSchemaRevisionRequest} request
     * @param {PublishedSchemas.RequestOptions} requestOptions - Request-specific configuration.
     *
     * @throws {@link Truvity.BadRequestError}
     * @throws {@link Truvity.UnauthorizedError}
     * @throws {@link Truvity.ForbiddenError}
     * @throws {@link Truvity.NotFoundError}
     * @throws {@link Truvity.TooManyRequestsError}
     * @throws {@link Truvity.InternalServerError}
     *
     * @example
     *     await client.publishedSchemas.publishedSchemaRevision("id", 1)
     */
    public publishedSchemaRevision(
        id: string,
        revision: number,
        request: Truvity.PublishedSchemaRevisionRequest = {},
        requestOptions?: PublishedSchemas.RequestOptions,
    ): core.HttpResponsePromise<Truvity.ResourcePublishedCredentialSchema> {
        return core.HttpResponsePromise.fromPromise(
            this.__publishedSchemaRevision(id, revision, request, requestOptions),
        );
    }

    private async __publishedSchemaRevision(
        id: string,
        revision: number,
        request: Truvity.PublishedSchemaRevisionRequest = {},
        requestOptions?: PublishedSchemas.RequestOptions,
    ): Promise<core.WithRawResponse<Truvity.ResourcePublishedCredentialSchema>> {
        const { ifNoneMatch } = request;
        var _headers: core.Fetcher.Args['headers'] = mergeHeaders(
            this._options?.headers,
            mergeOnlyDefinedHeaders({
                'If-None-Match': ifNoneMatch != null ? ifNoneMatch : undefined,
                ...(await this._getCustomAuthorizationHeaders()),
            }),
            requestOptions?.headers,
        );
        const _response = await (this._options.fetcher ?? core.fetcher)({
            url: core.url.join(
                (await core.Supplier.get(this._options.baseUrl)) ??
                    (await core.Supplier.get(this._options.environment)) ??
                    environments.TruvityEnvironment.Default,
                `published-schemas/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revision)}`,
            ),
            method: 'GET',
            headers: _headers,
            queryParameters: requestOptions?.queryParams,
            timeoutMs: requestOptions?.timeoutInSeconds != null ? requestOptions.timeoutInSeconds * 1000 : 60000,
            maxRetries: requestOptions?.maxRetries,
            abortSignal: requestOptions?.abortSignal,
        });
        if (_response.ok) {
            return {
                data: serializers.ResourcePublishedCredentialSchema.parseOrThrow(_response.body, {
                    unrecognizedObjectKeys: 'passthrough',
                    allowUnrecognizedUnionMembers: true,
                    allowUnrecognizedEnumValues: true,
                    breadcrumbsPrefix: ['response'],
                }),
                rawResponse: _response.rawResponse,
            };
        }

        if (_response.error.reason === 'status-code') {
            switch (_response.error.statusCode) {
                case 400:
                    throw new Truvity.BadRequestError(
                        serializers.BadRequestProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 401:
                    throw new Truvity.UnauthorizedError(
                        serializers.UnauthorizedProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 403:
                    throw new Truvity.ForbiddenError(
                        serializers.ForbiddenProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 404:
                    throw new Truvity.NotFoundError(
                        serializers.NotFoundProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 429:
                    throw new Truvity.TooManyRequestsError(
                        serializers.TooManyRequestsProblems.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                case 500:
                    throw new Truvity.InternalServerError(
                        serializers.ProblemInternalServerError.parseOrThrow(_response.error.body, {
                            unrecognizedObjectKeys: 'passthrough',
                            allowUnrecognizedUnionMembers: true,
                            allowUnrecognizedEnumValues: true,
                            breadcrumbsPrefix: ['response'],
                        }),
                        _response.rawResponse,
                    );
                default:
                    throw new errors.TruvityError({
                        statusCode: _response.error.statusCode,
                        body: _response.error.body,
                        rawResponse: _response.rawResponse,
                    });
            }
        }

        switch (_response.error.reason) {
            case 'non-json':
                throw new errors.TruvityError({
                    statusCode: _response.error.statusCode,
                    body: _response.error.rawBody,
                    rawResponse: _response.rawResponse,
                });
            case 'timeout':
                throw new errors.TruvityTimeoutError(
                    'Timeout exceeded when calling GET /published-schemas/{id}/revisions/{revision}.',
                );
            case 'unknown':
                throw new errors.TruvityError({
                    message: _response.error.errorMessage,
                    rawResponse: _response.rawResponse,
                });
        }
    }

    protected async _getCustomAuthorizationHeaders() {
        const apiKeyValue = await core.Supplier.get(this._options.apiKey);
        return { 'X-API-KEY': apiKeyValue };
    }
}
